import * as THREE from 'three'
import type { AssetKind, Disposable } from '@/shared/types/core'

/**
 * 视口作用域下的辅助根节点集合。
 */
export interface ViewHelperRoots {
  /** 工具类辅助对象的视口子根 */
  toolsRoot: THREE.Group
  /** Overlay 叠加层的视口子根 */
  overlayRoot: THREE.Group
  /** 调试可视化对象的视口子根 */
  debugRoot: THREE.Group
}

/**
 * 统一场景图管理 —— 管理普通资产、CAD 专用 pass 以及各类辅助 root。
 */
export class SceneGraphService implements Disposable {
  /** 主场景，承载点云、GSplat 与工具辅助对象 */
  readonly mainScene: THREE.Scene
  /** Overlay 场景，用于始终叠加在主场景之上的 UI 对象 */
  readonly overlayScene: THREE.Scene
  /** CAD 专用渲染场景，避免 CAD contributor 每帧移动 cadRoot。 */
  readonly cadRenderScene: THREE.Scene

  // 内建 root
  /** 点云资产挂载根 */
  readonly pointCloudRoot: THREE.Group
  /** 3DGS 资产挂载根 */
  readonly gaussianSplatRoot: THREE.Group
  /** CAD 资产挂载根 */
  readonly cadRoot: THREE.Group
  /** 工具辅助对象挂载根 */
  readonly toolsRoot: THREE.Group
  /** Overlay 层辅助对象挂载根，位于 overlayScene */
  readonly overlayRoot: THREE.Group
  /** 调试可视化对象挂载根 */
  readonly debugRoot: THREE.Group

  // 动态 root 注册表
  private roots = new Map<string, THREE.Group>()
  private viewHelperRoots = new Map<string, ViewHelperRoots>()

  constructor() {
    this.mainScene = new THREE.Scene()
    this.overlayScene = new THREE.Scene()
    this.cadRenderScene = new THREE.Scene()

    this.pointCloudRoot = new THREE.Group()
    this.pointCloudRoot.name = 'pointCloudRoot'

    this.gaussianSplatRoot = new THREE.Group()
    this.gaussianSplatRoot.name = 'gaussianSplatRoot'

    this.cadRoot = new THREE.Group()
    this.cadRoot.name = 'cadRoot'

    this.toolsRoot = new THREE.Group()
    this.toolsRoot.name = 'toolsRoot'

    this.overlayRoot = new THREE.Group()
    this.overlayRoot.name = 'overlayRoot'

    this.debugRoot = new THREE.Group()
    this.debugRoot.name = 'debugRoot'

    // 主场景承载普通资产与工具；CAD root 永久挂载到 CAD 专用渲染场景。
    this.mainScene.add(this.pointCloudRoot, this.gaussianSplatRoot, this.toolsRoot, this.debugRoot)
    this.cadRenderScene.add(this.cadRoot)

    // overlayRoot 挂载到 overlayScene
    this.overlayScene.add(this.overlayRoot)

    // 注册到内部表
    this.roots.set('pointCloud', this.pointCloudRoot)
    this.roots.set('gaussian-splat', this.gaussianSplatRoot)
    this.roots.set('cad', this.cadRoot)
  }

  /**
   * 按 AssetKind 返回对应 root。
   *
   * @param kind - 资产种类
   * @returns 对应根 Group；未注册时抛错
   */
  getRootForKind(kind: AssetKind): THREE.Group {
    const root = this.roots.get(kind)
    if (!root) {
      throw new Error(`未知的 AssetKind: "${kind}"，请先调用 registerRoot 注册`)
    }
    return root
  }

  /**
   * 设置场景背景。
   *
   * @param color - 背景 Color 或 Texture；传 null 置空
   */
  setBackground(color: THREE.Color | THREE.Texture | null): void {
    this.mainScene.background = color
  }

  /**
   * 动态注册资产根节点，已注册则返回已有 Group。
   *
   * @param kind - 资产种类标识，不得为空
   * @returns 对应根 Group
   */
  registerRoot(kind: string): THREE.Group {
    if (!kind || !kind.trim()) {
      throw new Error('SceneGraphService: kind 不能为空')
    }
    const existing = this.roots.get(kind)
    if (existing) return existing
    const group = new THREE.Group()
    group.name = `${kind}Root`
    this.mainScene.add(group)
    this.roots.set(kind, group)
    return group
  }

  /**
   * 查询已注册 root。
   *
   * @param kind - 资产种类标识
   * @returns 对应根 Group，未注册返回 undefined
   */
  getRoot(kind: string): THREE.Group | undefined {
    return this.roots.get(kind)
  }

  /**
   * 创建视口作用域的辅助根节点集合，若已存在则复用。
   *
   * @param viewId - 目标视口 ID，不得为空
   * @returns 视口辅助根集合
   */
  createViewHelperRoots(viewId: string): ViewHelperRoots {
    if (!viewId.trim()) {
      throw new Error('SceneGraphService: viewId 不能为空')
    }

    const existing = this.viewHelperRoots.get(viewId)
    if (existing) {
      return existing
    }

    const helperRoots: ViewHelperRoots = {
      toolsRoot: new THREE.Group(),
      overlayRoot: new THREE.Group(),
      debugRoot: new THREE.Group(),
    }
    helperRoots.toolsRoot.name = `toolsRoot/view:${viewId}`
    helperRoots.overlayRoot.name = `overlayRoot/view:${viewId}`
    helperRoots.debugRoot.name = `debugRoot/view:${viewId}`

    this.toolsRoot.add(helperRoots.toolsRoot)
    this.overlayRoot.add(helperRoots.overlayRoot)
    this.debugRoot.add(helperRoots.debugRoot)
    this.viewHelperRoots.set(viewId, helperRoots)

    return helperRoots
  }

  /**
   * 获取视口作用域的辅助根节点集合。
   *
   * @param viewId - 目标视口 ID
   * @returns 视口辅助根集合，未创建返回 undefined
   */
  getViewHelperRoots(viewId: string): ViewHelperRoots | undefined {
    return this.viewHelperRoots.get(viewId)
  }

  /**
   * 移除视口作用域的辅助根节点集合，并清理其子节点。
   *
   * @param viewId - 目标视口 ID
   * @returns 成功移除返回 true；不存在返回 false
   */
  removeViewHelperRoots(viewId: string): boolean {
    const roots = this.viewHelperRoots.get(viewId)
    if (!roots) {
      return false
    }

    roots.toolsRoot.clear()
    roots.overlayRoot.clear()
    roots.debugRoot.clear()
    roots.toolsRoot.removeFromParent()
    roots.overlayRoot.removeFromParent()
    roots.debugRoot.removeFromParent()
    this.viewHelperRoots.delete(viewId)
    return true
  }

  /** 清理场景图 */
  dispose(): void {
    for (const viewId of Array.from(this.viewHelperRoots.keys())) {
      this.removeViewHelperRoots(viewId)
    }
    while (this.mainScene.children.length > 0) {
      this.mainScene.remove(this.mainScene.children[0])
    }
    while (this.overlayScene.children.length > 0) {
      this.overlayScene.remove(this.overlayScene.children[0])
    }
    while (this.cadRenderScene.children.length > 0) {
      this.cadRenderScene.remove(this.cadRenderScene.children[0])
    }
    this.roots.clear()
  }
}
